"use client"

import React, { createContext, useContext } from "react"
import type { PaginationMetadata } from "@/schema"
import type {
  BulkImportItem,
  BulkImportResponse,
  CommissionerApplyFiltersResponse,
  CommissionerApplyMappingsResponse,
  CommissionerBulkUpdateItem,
  CommissionerDataImportResponse,
  CommissionerFilterOptionsResponse,
  CommissionerFilterSelection,
  CommissionerHouseholdResponse,
  CommissionerReviewItem,
  HouseholdCreateParams,
  ImportAs,
  RetryImportResponse,
} from "@/schema/commissioner-data-import"
import type { UseInfiniteQueryResult, UseMutationResult, UseQueryResult } from "@tanstack/react-query"

import type { ReviewItemsParams } from "@/hooks/api/commissioner/data-imports/api"

export interface DataImportApi {
  useFetchDataImport: (dataImportId: string | null) => UseQueryResult<CommissionerDataImportResponse, Error>

  useFetchFilterOptions: (
    dataImportId: string | null
  ) => UseQueryResult<CommissionerFilterOptionsResponse | undefined, Error>

  useFetchReviewItems: (
    dataImportId: string | null,
    params?: ReviewItemsParams,
    opts?: { poll?: boolean }
  ) => UseInfiniteQueryResult<{ review_items: CommissionerReviewItem[]; pagination: PaginationMetadata }, Error>

  useApplyFilters: (
    dataImportId: string
  ) => UseMutationResult<
    CommissionerApplyFiltersResponse,
    Error,
    { import_mode: "all" | "filtered"; import_filters?: CommissionerFilterSelection[] }
  >

  useApplyMappings: (dataImportId: string) => UseMutationResult<
    CommissionerApplyMappingsResponse,
    Error,
    {
      client_filters?: CommissionerFilterSelection[]
      past_client_filters?: CommissionerFilterSelection[]
      prospect_filters?: CommissionerFilterSelection[]
      import_remaining_items_as?: "other_contact" | "prospect"
    }
  >

  useBulkUpdateReviewItems: (dataImportId: string) => UseMutationResult<void, Error, CommissionerBulkUpdateItem[]>

  useStartImport: (
    dataImportId: string
  ) => UseMutationResult<{ data_import: { id: string; status: string; wizard_step: string } }, Error, void>

  useUpdateDataImport: (
    dataImportId: string
  ) => UseMutationResult<CommissionerDataImportResponse, Error, { wizard_step: "filter" | "data_mappings" | "review" }>

  useFetchHousehold: (
    dataImportId: string | null,
    householdId: string | null
  ) => UseQueryResult<CommissionerHouseholdResponse, Error>

  useUpdateHousehold: (
    dataImportId: string
  ) => UseMutationResult<
    CommissionerHouseholdResponse,
    Error,
    { householdId: string; data: { import_as?: ImportAs; manual_create_params?: HouseholdCreateParams } }
  >

  useRetryHouseholdImport: (
    dataImportId: string
  ) => UseMutationResult<
    RetryImportResponse,
    Error,
    { householdId: string; manualCreateParams?: HouseholdCreateParams }
  >

  useRetryBusinessImport: (
    dataImportId: string
  ) => UseMutationResult<RetryImportResponse, Error, { businessId: string }>

  useBulkImport: (dataImportId: string) => UseMutationResult<BulkImportResponse, Error, { items: BulkImportItem[] }>
}

const DataImportApiContext = createContext<DataImportApi | null>(null)

export function DataImportApiProvider({ api, children }: { api: DataImportApi; children: React.ReactNode }) {
  return <DataImportApiContext.Provider value={api}>{children}</DataImportApiContext.Provider>
}

export function useDataImportApi(): DataImportApi {
  const ctx = useContext(DataImportApiContext)
  if (!ctx) {
    throw new Error("useDataImportApi must be used within a DataImportApiProvider")
  }
  return ctx
}
